import { useEffect, useRef } from 'react'

const BAR_COUNT = 32
const BAR_GAP = 3
const BAR_RADIUS = 2
const MIN_BAR_HEIGHT = 2

interface AudioVisualizerProps {
  deviceId: string
  enabled: boolean
  className?: string
}

export function AudioVisualizer({ deviceId, enabled, className }: AudioVisualizerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (!enabled || !deviceId) return

    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let cancelled = false
    let audioContext: AudioContext | null = null
    let stream: MediaStream | null = null
    let frameId: number

    async function start() {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: { exact: deviceId } },
        })

        if (cancelled) {
          for (const t of stream.getTracks()) t.stop()
          return
        }

        audioContext = new AudioContext()
        const source = audioContext.createMediaStreamSource(stream)
        const analyser = audioContext.createAnalyser()
        analyser.fftSize = 256
        analyser.smoothingTimeConstant = 0.8
        source.connect(analyser)

        const dataArray = new Uint8Array(analyser.frequencyBinCount)

        function draw() {
          frameId = requestAnimationFrame(draw)

          const canvas = canvasRef.current
          if (!canvas || !ctx) return

          const rect = canvas.getBoundingClientRect()
          const dpr = window.devicePixelRatio || 1
          canvas.width = rect.width * dpr
          canvas.height = rect.height * dpr
          ctx.scale(dpr, dpr)

          const width = rect.width
          const height = rect.height

          ctx.clearRect(0, 0, width, height)

          analyser.getByteFrequencyData(dataArray)

          const barWidth = (width - BAR_GAP * (BAR_COUNT - 1)) / BAR_COUNT
          const binSize = Math.floor(dataArray.length / BAR_COUNT)

          for (let i = 0; i < BAR_COUNT; i++) {
            let sum = 0
            for (let j = 0; j < binSize; j++) {
              sum += dataArray[i * binSize + j] ?? 0
            }
            const avg = sum / binSize / 255

            const barHeight = Math.max(MIN_BAR_HEIGHT, avg * height * 0.9)
            const x = i * (barWidth + BAR_GAP)
            const y = (height - barHeight) / 2

            const opacity = 0.3 + avg * 0.7
            ctx.fillStyle = `oklch(0.7 0.18 55 / ${opacity})`

            ctx.beginPath()
            ctx.roundRect(x, y, barWidth, barHeight, BAR_RADIUS)
            ctx.fill()
          }
        }

        draw()
      } catch {
        // Device unavailable or permission denied
      }
    }

    start()

    return () => {
      cancelled = true
      if (frameId) cancelAnimationFrame(frameId)
      if (audioContext) audioContext.close()
      if (stream) {
        for (const t of stream.getTracks()) t.stop()
      }
    }
  }, [deviceId, enabled])

  return <canvas ref={canvasRef} className={className} style={{ width: '100%', height: '100%' }} />
}
